import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'

const clientPortalAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {'Content-Type': 'application/json'},
})

const schema = Yup.object().shape({
  password: Yup.string().min(8, 'Mínimo de 8 caracteres').required('Senha é obrigatória'),
  password_confirmation: Yup.string()
    .required('Confirmação de senha é obrigatória')
    .oneOf([Yup.ref('password')], 'As senhas não coincidem'),
})

export function ClientPortalResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const formik = useFormik({
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validationSchema: schema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setErrorMessage('')
      try {
        const {data} = await clientPortalAxios.post('/api/client-portal/auth/reset-password', {
          token: searchParams.get('token'),
          email: searchParams.get('email'),
          password: values.password,
          password_confirmation: values.password_confirmation,
        })
        navigate('/auth/area-do-cliente/login', {
          replace: true,
          state: {successMessage: data?.message || 'Senha alterada com sucesso.'},
        })
      } catch (error: any) {
        setErrorMessage(
          error.response?.data?.message ||
            'Não foi possível alterar a senha. Peça um novo link e tente novamente.'
        )
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <AuthAuroraShell>
      <AuthBrandHeader badge='Portal do Cliente' />

      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_client_portal_new_password_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>Alterar senha</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            Digite a nova senha e a confirmação para alterar sua senha.
          </div>
        </div>

        {errorMessage && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{errorMessage}</div>
          </div>
        )}

        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6' htmlFor='client-portal-new-password'>
            Senha
          </label>
          <input
            id='client-portal-new-password'
            type='password'
            autoComplete='new-password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.password && formik.errors.password},
              {'is-valid': formik.touched.password && !formik.errors.password}
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
          <div className='text-muted mt-2'>A senha deve ter pelo menos 8 caracteres.</div>
        </div>

        <div className='fv-row mb-5'>
          <label
            className='form-label fw-bolder text-dark fs-6'
            htmlFor='client-portal-new-password-confirmation'
          >
            Confirme a senha
          </label>
          <input
            id='client-portal-new-password-confirmation'
            type='password'
            autoComplete='new-password'
            {...formik.getFieldProps('password_confirmation')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid':
                  formik.touched.password_confirmation && formik.errors.password_confirmation,
              },
              {
                'is-valid':
                  formik.touched.password_confirmation && !formik.errors.password_confirmation,
              }
            )}
          />
          {formik.touched.password_confirmation && formik.errors.password_confirmation && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password_confirmation}</span>
              </div>
            </div>
          )}
        </div>

        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            className='btn btn-primary me-4'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span className='indicator-label'>Enviar</span>
            {loading && (
              <span className='indicator-progress'>
                Aguarde...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/area-do-cliente/login'>
            <button type='button' className='btn btn-light'>
              Cancelar
            </button>
          </Link>
        </div>
      </form>
    </AuthAuroraShell>
  )
}
